import { isAdminOrAbove, isStaffOrAbove, UserRole as UserRoleEnum } from "@excel-center/shared";
import { Briefcase, Calendar, LayoutDashboard, User, Users } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { UserRole } from "@/src/auth/types/auth.types";
import type { TopBarExtraItem } from "@/src/common/components/top-bar/top-bar";
import { DASHBOARD_ROUTES } from "@/src/dashboard/constants/dashboard-routes.constants";
import { JOBS_ROUTES } from "@/src/jobs/constants/jobs-routes.constants";
import { PROFILE_ROUTES } from "@/src/profile/constants/profile-routes.constants";
import { STUDENTS_ROUTES } from "@/src/students/constants/students-routes.constants";

/**
 * Role-aware extra items injected at the top of the user dropdown
 * (Settings + Sign Out remain at the bottom, always). Students get
 * My Profile / My Schedule shortcuts; general staff get Students only;
 * admin+ get Dashboard / Students / Jobs Management.
 */
export function useTopBarExtraItems(role: UserRole | null): readonly TopBarExtraItem[] {
	const { t } = useTranslation("common");
	return useMemo<readonly TopBarExtraItem[]>(() => {
		if (isAdminOrAbove(role)) {
			return [
				{ id: "dashboard", label: t("nav.dashboard"), to: DASHBOARD_ROUTES.dashboard, icon: LayoutDashboard },
				{ id: "students", label: t("nav.students"), to: STUDENTS_ROUTES.students, icon: Users },
				{ id: "jobs_manage", label: t("nav.job_management"), to: JOBS_ROUTES.appJobsManage, icon: Briefcase },
			];
		}
		if (isStaffOrAbove(role)) {
			return [{ id: "students", label: t("nav.students"), to: STUDENTS_ROUTES.students, icon: Users }];
		}
		if (role === UserRoleEnum.STUDENT) {
			return [
				{ id: "my_profile", label: t("nav.my_profile"), to: PROFILE_ROUTES.myProfile, icon: User },
				{ id: "my_schedule", label: t("nav.my_schedule"), to: PROFILE_ROUTES.mySchedule, icon: Calendar },
			];
		}
		return [];
	}, [role, t]);
}
